/**
 * Heuristic detection for browsers that cannot run the Sumsub liveness/facial flow —
 * most notably iOS Safari with **Lockdown Mode** enabled.
 *
 * Lockdown Mode (and any iOS browser, since they all use WebKit) disables WebAssembly and
 * `getUserMedia` — exactly the capabilities the Sumsub WebSDK needs for camera + on-device
 * liveness. We use their absence on iOS as a signal.
 *
 * NOTE: this is a heuristic, not an official API. There is no way to read the Lockdown Mode
 * flag directly. Always verify behaviour on a real iPhone with Lockdown Mode on before
 * relying on it.
 */

export interface RestrictedBrowserResult {
  isRestricted: boolean;
  /** Which expected capabilities were missing — useful as Datadog log context. */
  reasons: string[];
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPhone/iPad/iPod, plus iPadOS which reports as Mac but exposes touch.
  const iOSLike = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = /Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document;
  return iOSLike || iPadOS;
}

export function detectRestrictedBrowser(): RestrictedBrowserResult {
  const reasons: string[] = [];

  // Server-side / non-browser render: never flag as restricted.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { isRestricted: false, reasons };
  }

  if (typeof WebAssembly === 'undefined') {
    reasons.push('WebAssembly unavailable');
  }
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    reasons.push('getUserMedia unavailable');
  }

  // Only treat missing capabilities as "restricted" on iOS, where a modern browser would
  // otherwise have them — this avoids false positives on unusual desktop setups.
  const isRestricted = isIOS() && reasons.length > 0;

  return { isRestricted, reasons };
}
